import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { trackClick } from '../utils/analytics';

interface SocialMenuProps {
  theme?: 'cyberpunk' | 'neon' | 'nexus' | 'red' | 'blue' | 'pink' | 'yellow' | 'orange' | 'purple' | 'cyan' | 'green' | 'gold';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const socialLinks = [
  { icon: 'fab fa-reddit', label: 'Reddit', url: 'https://www.reddit.com/u/Major-Design-5332' },
  { icon: 'fab fa-instagram', label: 'Instagram', url: 'https://www.instagram.com/a5omic' },
  { icon: 'fab fa-tiktok', label: 'TikTok', url: 'https://www.tiktok.com/@a5omic' },
  { icon: 'fab fa-discord', label: 'Discord', url: 'https://discord.gg/GJJrgsh4Tx' },
  { icon: 'fab fa-spotify', label: 'Spotify', url: 'https://open.spotify.com/artist/7L3rQbwQVEGa71Ign85jyd' },
  { icon: 'fab fa-github', label: 'GitHub', url: 'https://github.com/Atomics-hub' },
  { icon: 'fab fa-facebook', label: 'Facebook', url: 'https://www.facebook.com/people/Tom-Ryan/100084682343532/' },
];

const getThemeColors = (theme: string) => {
  switch (theme) {
    case 'neon':
      return {
        primary: '#00f7ff',
        secondary: '#ffff00',
        accent: '#ff69b4',
        background: 'rgba(0, 15, 30, 0.85)',
        border: 'rgba(0, 247, 255, 0.5)'
      };
    case 'nexus':
      return {
        primary: '#ffd700',
        secondary: '#00e5ff',
        accent: '#fc00ff',
        background: 'rgba(25, 25, 35, 0.85)',
        border: 'rgba(255, 215, 0, 0.5)'
      };
    case 'red':
      return {
        primary: '#ff4444',
        secondary: '#ff8888',
        accent: '#ffaaaa',
        background: 'rgba(30, 15, 15, 0.85)',
        border: 'rgba(255, 68, 68, 0.5)'
      };
    case 'blue':
      return {
        primary: '#4488ff',
        secondary: '#88bbff',
        accent: '#aaccff',
        background: 'rgba(15, 20, 30, 0.85)',
        border: 'rgba(68, 136, 255, 0.5)'
      };
    case 'pink':
      return {
        primary: '#ff44aa',
        secondary: '#ff88cc',
        accent: '#ffaadd',
        background: 'rgba(30, 15, 25, 0.85)',
        border: 'rgba(255, 68, 170, 0.5)'
      };
    case 'yellow':
      return {
        primary: '#ffcc00',
        secondary: '#ffdd44',
        accent: '#ffee88',
        background: 'rgba(30, 25, 10, 0.85)',
        border: 'rgba(255, 204, 0, 0.5)'
      };
    case 'orange':
      return {
        primary: '#ff5722',
        secondary: '#ff8a65',
        accent: '#ffab91',
        background: 'rgba(30, 20, 15, 0.85)',
        border: 'rgba(255, 87, 34, 0.5)'
      };
    case 'purple':
      return {
        primary: '#b24bf3',
        secondary: '#d084ff',
        accent: '#e5b3ff',
        background: 'rgba(25, 15, 35, 0.85)',
        border: 'rgba(178, 75, 243, 0.5)'
      };
    case 'cyan':
      return {
        primary: '#00e5ff',
        secondary: '#4df8ff',
        accent: '#80fbff',
        background: 'rgba(10, 25, 30, 0.85)',
        border: 'rgba(0, 229, 255, 0.5)'
      };
    case 'green':
      return {
        primary: '#00ff88',
        secondary: '#4dffaa',
        accent: '#80ffc4',
        background: 'rgba(10, 30, 20, 0.85)',
        border: 'rgba(0, 255, 136, 0.5)'
      };
    case 'gold':
      return {
        primary: '#ffd700',
        secondary: '#ffe55c',
        accent: '#fff099',
        background: 'rgba(30, 25, 10, 0.85)',
        border: 'rgba(255, 215, 0, 0.5)'
      };
    default: // cyberpunk
      return {
        primary: '#00ff00',
        secondary: '#ff0080',
        accent: '#00ffff',
        background: 'rgba(15, 15, 25, 0.85)',
        border: 'rgba(0, 255, 0, 0.5)'
      };
  }
};

const getPosition = (position: string) => {
  switch (position) {
    case 'top-left':
      return { top: '2rem', left: '2rem', transformOrigin: 'left' };
    case 'bottom-right':
      return { bottom: '2rem', right: '2rem', transformOrigin: 'right' };
    case 'bottom-left':
      return { bottom: '2rem', left: '2rem', transformOrigin: 'left' };
    default: // top-right
      return { top: '2rem', right: '2rem', transformOrigin: 'right' };
  }
};

const SocialMenu = styled.div<{ $isOpen: boolean; $theme: string; $position: string }>`
  position: fixed;
  ${props => {
    const pos = getPosition(props.$position);
    const colors = getThemeColors(props.$theme);
    const isLeft = props.$position.includes('left');
    const offset = isLeft ? 
      (props.$isOpen ? '5.5rem' : '-300px') : 
      (props.$isOpen ? '5.5rem' : '-300px');
    
    if (isLeft) {
      return `
        ${pos.top ? `top: ${pos.top};` : ''}
        ${pos.bottom ? `bottom: ${pos.bottom};` : ''}
        left: ${props.$isOpen ? pos.left : '-300px'};
      `;
    } else {
      return `
        ${pos.top ? `top: ${pos.top};` : ''}
        ${pos.bottom ? `bottom: ${pos.bottom};` : ''}
        right: ${props.$isOpen ? offset : '-300px'};
      `;
    }
  }}
  background: ${props => getThemeColors(props.$theme).background};
  border: 1px solid ${props => getThemeColors(props.$theme).border};
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  backdrop-filter: blur(10px);
  display: flex;
  gap: 0.75rem;
  box-shadow: 0 0 20px ${props => getThemeColors(props.$theme).primary}40;
`;

const SocialToggle = styled.button<{ $theme: string; $position: string }>`
  position: fixed;
  ${props => {
    const pos = getPosition(props.$position);
    return `
      ${pos.top ? `top: ${pos.top};` : ''}
      ${pos.bottom ? `bottom: ${pos.bottom};` : ''}
      ${pos.left ? `left: ${pos.left};` : ''}
      ${pos.right ? `right: ${pos.right};` : ''}
    `;
  }}
  background: ${props => getThemeColors(props.$theme).background};
  border: 1px solid ${props => getThemeColors(props.$theme).border};
  border-radius: 8px;
  width: 2.5rem;
  height: 2.5rem;
  color: ${props => getThemeColors(props.$theme).primary};
  font-family: 'VT323', monospace;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 11;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${props => getThemeColors(props.$theme).secondary};
    box-shadow: 0 0 20px ${props => getThemeColors(props.$theme).primary}40;
    border-color: ${props => getThemeColors(props.$theme).primary};
    transform: scale(1.05);
  }
`;

const SocialLink = styled.a<{ $theme: string }>`
  color: ${props => getThemeColors(props.$theme).primary};
  font-size: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  padding: 0.5rem;
  border-radius: 4px;
  background: ${props => getThemeColors(props.$theme).background.replace('0.85', '0.9')};
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => getThemeColors(props.$theme).border};
  text-decoration: none;

  &:hover {
    color: ${props => getThemeColors(props.$theme).secondary};
    transform: translateY(-3px);
    box-shadow: 0 0 20px ${props => getThemeColors(props.$theme).primary}80;
    border-color: ${props => getThemeColors(props.$theme).primary};
  }
`;

const YouTubeWrapper = styled.div`
  position: relative;
`;

const YouTubePopup = styled.div<{ $visible: boolean; $theme: string }>`
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  gap: 0.4rem;
  background: ${props => getThemeColors(props.$theme).background};
  border: 1px solid ${props => getThemeColors(props.$theme).border};
  border-radius: 6px;
  padding: 0.5rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 15px ${props => getThemeColors(props.$theme).primary}30;
  z-index: 20;
  white-space: nowrap;
`;

const YouTubePopupLink = styled.a<{ $theme: string }>`
  color: ${props => getThemeColors(props.$theme).primary};
  font-family: 'VT323', monospace;
  font-size: 0.85rem;
  text-decoration: none;
  padding: 0.3rem 0.6rem;
  border-radius: 3px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => getThemeColors(props.$theme).primary}20;
    color: ${props => getThemeColors(props.$theme).secondary};
  }
`;

const StyledSocialMenu: React.FC<SocialMenuProps> = ({
  theme = 'cyberpunk',
  position = 'top-right'
}) => {
  const [isSocialOpen, setIsSocialOpen] = useState(false);
  const [ytPopup, setYtPopup] = useState(false);
  const ytRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ytRef.current && !ytRef.current.contains(e.target as Node)) {
        setYtPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSocialClick = (platform: { label: string; url: string }) => {
    trackClick('styled_social_click', {
      platform_name: platform.label,
      theme: theme,
      position: position
    });
    window.open(platform.url, '_blank');
  };

  return (
    <>
      <SocialToggle
        $theme={theme}
        $position={position}
        onClick={() => setIsSocialOpen(!isSocialOpen)}
      >
        <i className={`fas fa-${isSocialOpen ? 'times' : 'share-alt'}`} />
      </SocialToggle>
      <SocialMenu $isOpen={isSocialOpen} $theme={theme} $position={position}>
        <SocialLink
          $theme={theme}
          href="https://x.com/A5omic"
          onClick={(e) => { e.preventDefault(); handleSocialClick({ label: 'X', url: 'https://x.com/A5omic' }); }}
          title="X"
        >
          <i className="fab fa-x-twitter" />
        </SocialLink>
        <YouTubeWrapper ref={ytRef}>
          <SocialLink
            $theme={theme}
            href="#"
            onClick={(e) => { e.preventDefault(); setYtPopup(!ytPopup); }}
            title="YouTube"
          >
            <i className="fab fa-youtube" />
          </SocialLink>
          <YouTubePopup $visible={ytPopup} $theme={theme}>
            <YouTubePopupLink
              $theme={theme}
              href="https://youtube.com/@A5omic"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { trackClick('styled_social_click', { platform_name: 'YouTube Personal', theme, position }); setYtPopup(false); }}
            >
              Personal
            </YouTubePopupLink>
            <YouTubePopupLink
              $theme={theme}
              href="https://youtube.com/@A5omic_"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { trackClick('styled_social_click', { platform_name: 'YouTube Music', theme, position }); setYtPopup(false); }}
            >
              Music
            </YouTubePopupLink>
          </YouTubePopup>
        </YouTubeWrapper>
        {socialLinks.map((link) => (
          <SocialLink
            key={link.label}
            $theme={theme}
            href={link.url}
            onClick={(e) => {
              e.preventDefault();
              handleSocialClick(link);
            }}
            title={link.label}
          >
            <i className={link.icon} />
          </SocialLink>
        ))}
      </SocialMenu>
    </>
  );
};

export default StyledSocialMenu;
